package com.eladfinish.bible.contest.screens.chapterComplete.features.createCompletions

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.ColorConstants.INDIGO_DARK
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.extensions.FONT_SERIF
import com.eladfinish.bible.contest.extensions.matches
import com.eladfinish.bible.contest.screens.chapterComplete.model.UserCompletionsModel
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.text.SpannedRange
import kotlinx.collections.immutable.toImmutableList

class UserCompletionsRenderer(
    private val removeNikudAndTeamimAndMakafUseCase: RemoveNikudAndTeamimAndMakafUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
) {

    /**
     * Renders user-created completions (toggleable blanks) 1:1 with the legacy Android logic:
     * - Same spacing rules (including makaf/sof-pasuk behavior).
     * - Optional newline after COLON_SPLITTER when eachVerseInNewLine is enabled.
     * - Legacy "vav ha'chibur" handling for blank rendering (keep 'ו' and shorten underline by 1).
     * - Adds spans:
     *   - Clickable for toggleable tokens (word OR underline).
     *   - Underline style (serif + indigo) when hidden.
     *   - "Otiot pasuk" relative-size spans via MakeOtiotPasukSmallerUseCase.
     */
    internal fun render(
        model: UserCompletionsModel,
        variableLengthUnderline: Boolean,
        eachVerseInNewLine: Boolean,
    ): SpannableStringKMP {

        val sb = StringBuilder()
        val spans = mutableListOf<SpannedRange>()

        val tokens = model.tokens
        val visibility = model.visibility

        for (i in tokens.indices) {
            var newLine = false

            val token = tokens[i]
            val currentWord = token.raw
            val visible = visibility.getOrNull(i) ?: true

            if (visible) {
                val start = sb.length
                sb.append(currentWord)
                val end = sb.length

                if (token.isToggleable) {
                    spans += SpannedRange(
                        spanType = SpanType.Clickable(
                            action = ClickAction.ToggleUserBlank(
                                tokenIndex = i,
                            ),
                        ),
                        textRange = TextRange(
                            start = start,
                            end = end,
                        ),
                    )
                }

                if (currentWord.endsWith(COLON_SPLITTER)) {
                    if (eachVerseInNewLine) {
                        sb.append("\n")
                        newLine = true
                    } else {
                        sb.append(" ")
                    }
                }
            } else {
                appendBlankLikeLegacy(
                    out = sb,
                    spans = spans,
                    originWord = currentWord,
                    variableLengthUnderline = variableLengthUnderline,
                    color = INDIGO_DARK,
                    typefaceFamily = FONT_SERIF,
                    onClick = ClickAction.ToggleUserBlank(
                        tokenIndex = i,
                    ),
                    isClickable = token.isToggleable,
                )
            }

            val nextWord = tokens.getOrNull(i + 1)?.raw
            val shouldAppendSpace =
                !newLine &&
                        currentWord != MAKAF.toString() &&
                        i + 1 != tokens.size &&
                        nextWord != null &&
                        !(COLON_SPLITTER + MAKAF).contains(nextWord)

            if (shouldAppendSpace) {
                sb.append(" ")
            }
        }

        if (sb.isNotEmpty() && sb.last() == ' ') {
            sb.setLength(sb.length - 1)
        }

        // Add "ot pasuk" shrinking spans on the final text (same idea as legacy makeOtiotPasukSmaller()).
        spans += makeOtiotPasukSmallerUseCase(
            charSequence = sb,
        )

        return SpannableStringKMP(
            charSequence = sb.toString(),
            spannedRangeList = spans.toImmutableList(),
        )
    }

    private fun appendBlankLikeLegacy(
        out: StringBuilder,
        spans: MutableList<SpannedRange>,
        originWord: String,
        variableLengthUnderline: Boolean,
        color: Color,
        typefaceFamily: String,
        onClick: ClickAction,
        isClickable: Boolean,
    ) {
        var lengthDelta = 0

        // Legacy behavior:
        // If the word starts with "vav ha'chibur" (including niqqud/taamim patterns),
        // keep the 'ו' visible and shorten the underline by 1.
        if (originWord.matches(
                regexString =
                    "^(וְ|וָ.ֳ|וִֽ?י|וּ|" +
                            "וַֽ" + NOT_ALEF_TAV_SPACE + "?" + ".ֲ" + "|" +
                            "וָ." + NOT_ALEF_TAV_SPACE + "*" + ".[ֶַ]." + "|" +
                            "וָ.ַ" + NOT_ALEF_TAV_SPACE +
                            ").*",
            ) ||
            originWord.matches(
                regexString =
                    "^" +
                            "וָ." + NOT_ALEF_TAV_SPACE + "*" +
                            "ֹ" + NOT_ALEF_TAV_SPACE + "*" + ".$",
            )
        ) {
            out.append("ו")
            lengthDelta--
        }

        val onlyLetters = removeNikudAndTeamimAndMakafUseCase(originWord)

        val underline = if (variableLengthUnderline) {
            val len = (lengthDelta + onlyLetters.length).coerceAtLeast(1)
            "_".repeat(len)
        } else {
            "_".repeat(5)
        }

        val start = out.length
        out.append(underline)
        val end = out.length

        // Style underline (serif + indigo), like your appendBlank() inspiration.
        spans += SpannedRange(
            spanType = SpanType.CustomTypeface(
                family = typefaceFamily,
            ),
            textRange = TextRange(
                start = start,
                end = end,
            ),
        )

        spans += SpannedRange(
            spanType = SpanType.ForegroundColor(
                color = color,
            ),
            textRange = TextRange(
                start = start,
                end = end,
            ),
        )

        if (isClickable) {
            spans += SpannedRange(
                spanType = SpanType.Clickable(
                    action = onClick,
                ),
                textRange = TextRange(
                    start = start,
                    end = end,
                ),
            )
        }
    }

    companion object {
        // You provided this constant.
        const val NOT_ALEF_TAV_SPACE = "[^א-ת${MAKAF}${COLON_SPLITTER}\\s]"
    }
}
